import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { LazyGrid } from 'components/common'
import ContactWorksApi from 'api/DataEntry/Landing/Contact/ContactWorks'
import { contactWorksColumnDefinitions } from './config/ContactWorksColumns'
import {
    setContactWorksFetchStatusAction,
    setContactWorksFetchResultsAction,
    setContactWorksCurrentPageAction,
    setContactWorksTotalPagesAction,
    setContactWorksFirstAction,
    setContactWorksRowsAction,
    setContactWorksFilterAction,
    setContactWorksSortAction,
    initContactWorksAction
} from './store/actions'
import {
    selectContactWorksFetchResults,
    selectContactWorksCurrentPage,
    selectContactWorksTotalPages,
    selectContactWorksRows,
    selectContactWorksFirst,
    selectContactWorksFetchStatus,
    selectContactWorksRequest
} from './store/selectors'

/**
 * Nested table component to display works for a contact using LazyGrid
 * Each contact instance maintains its own independent state in Redux (by contactId)
 * This allows multiple contacts to be expanded simultaneously
 * @param {Object} props
 * @param {Object} props.contact - The contact object containing works data
 * @param {string} props.apiBaseUrl - Base URL for the Membership API
 * @param {Function} props.onError - Error callback
 */
const ContactWorksNestedTable = ({ contact, apiBaseUrl, onError }) => {
    const dispatch = useDispatch()
    const { contactId, worksCount } = contact

    // Initialize state for this contact if it doesn't exist
    useEffect(() => {
        dispatch(initContactWorksAction(contactId))
    }, [contactId, dispatch])

    // Redux selectors - pass contactId to get this contact's specific data
    const worksResults = useSelector(selectContactWorksFetchResults(contactId))
    const currentPage = useSelector(selectContactWorksCurrentPage(contactId))
    const totalPages = useSelector(selectContactWorksTotalPages(contactId))
    const rows = useSelector(selectContactWorksRows(contactId))
    const first = useSelector(selectContactWorksFirst(contactId))
    const fetchStatus = useSelector(selectContactWorksFetchStatus(contactId))
    const worksRequest = useSelector(selectContactWorksRequest(contactId))

    // Create memoized selectors for this contact
    const selectorsForContact = useMemo(() => ({
        selectTotalPages: selectContactWorksTotalPages(contactId),
        selectFirst: selectContactWorksFirst(contactId),
        selectRows: selectContactWorksRows(contactId),
        selectFetchStatus: selectContactWorksFetchStatus(contactId)
    }), [contactId])

    // Create a memoized selector for this contact
    const requestSelectorForContact = useMemo(
        () => (state) => selectContactWorksRequest(contactId)(state),
        [contactId]
    )

    // Create memoized action creators bound to this contactId
    const boundActions = useMemo(() => ({
        setTotalPages: (pages) => setContactWorksTotalPagesAction(contactId, pages),
        setCurrentPage: (page) => setContactWorksCurrentPageAction(contactId, page),
        setFirst: (first) => setContactWorksFirstAction(contactId, first),
        setRows: (rows) => setContactWorksRowsAction(contactId, rows),
        setFilter: (filter) => setContactWorksFilterAction(contactId, filter),
        setSort: (sort) => setContactWorksSortAction(contactId, sort)
    }), [contactId])

    // Prepare data with unique IDs
    const worksResultList = (worksResults?.results || []).map((work, index) => ({
        ...work,
        uniqueId: `${contactId}_${work.workCode || ''}_${work.contributionType || ''}_${index}`
    }))
    const worksTotal = worksResults?.total || worksCount || 0

    return (
        <div className="p-3">
            <h5>Works ({worksTotal})</h5>
            <ContactWorksApi
                contactId={contactId}
                endpoint={apiBaseUrl}
                setStatusAction={setContactWorksFetchStatusAction}
                setResultsAction={setContactWorksFetchResultsAction}
                requestSelector={requestSelectorForContact}
            />
            <LazyGrid
                id={`contact_${contactId}_works_grid`}
                dataKey="uniqueId"
                data={worksResultList}
                totalCount={worksTotal}
                emptyMessage="No works found"
                columnDefinitions={contactWorksColumnDefinitions}
                selectTotalPages={selectorsForContact.selectTotalPages}
                selectFirst={selectorsForContact.selectFirst}
                selectRows={selectorsForContact.selectRows}
                fetchStatusSelector={selectorsForContact.selectFetchStatus}
                setTotalPagesAction={boundActions.setTotalPages}
                setCurrentPageAction={boundActions.setCurrentPage}
                setFirstAction={boundActions.setFirst}
                setRowsAction={boundActions.setRows}
                setFilterAction={boundActions.setFilter}
                setSortAction={boundActions.setSort}
                currentPage={currentPage}
                scrollHeight="400px"
            />
        </div>
    )
}

export default ContactWorksNestedTable

